import Helmet from "react-helmet";
import React, { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-hot-toast";
import { getEditorWelcomeForRole } from "./OnboardingFlow/onboardingFlowConfig.ts";
import type { OnboardingRole } from "./OnboardingFlow/onboardingFlowConfig.ts";
import TrialActivationModal from "./OnboardingFlow/TrialActivationModal.tsx";
import FieldEditorHeader from "./drillView/fieldEditorHeader.tsx";
import FieldEditorTimeline from "./drillView/fieldEditorTimeline.tsx";
import FieldEditorModeSelect from "./drillView/fieldEditorModeSelect.tsx";
import FieldEditorRightNav from "./drillView/fieldEditorRIghtNav.tsx";
import { DrillDocumentData } from "./hooks/useDrillDocument.tsx";
import FieldEditorMenuBar from "./drillView/fieldEditorMenuBar.tsx";
import AudioSettingsModal from "../components/drillView/modals/audioSettingsModal.tsx";
import FieldSettingsModal from "../components/drillView/modals/fieldSettingsModal.js";
import PerformerModal from "../components/drillView/modals/performerModel.js";
import CoordinateModal from "../components/drillView/modals/coordinateModal.js";
import PrintPreview from "./drillView/printPreview.tsx";
import BulkEditPerformerLabelModal from "../components/drillView/modals/bulkPerformerModal.js";
import { ShortcutsList } from "./keyboardShortcuts.tsx";
import { FieldEditorProvider } from "./hooks/FieldEditorContext.tsx";
import {
  useFieldEditorData,
  useDrillViewData,
  useDrillDocumentData,
  useExportData,
} from "./hooks/useFieldEditorShared.tsx";
import FieldEditorContextPopup from "./drillView/fieldEditorContextPopup.tsx";
import EditorOnboardingOverlay from "./onboarding/EditorOnboardingOverlay.tsx";
import {
  EDITOR_ONBOARDING_STORAGE_COMPLETED,
  EDITOR_ONBOARDING_STORAGE_DISMISSED,
} from "./onboarding/editorOnboardingSteps.tsx";

function FieldEditor3Content() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const [storageReady, setStorageReady] = useState(false);

  useEffect(() => {
    setStorageReady(true);
  }, []);

  const showEditorOnboarding = useMemo(() => {
    if (!storageReady) return false;
    if (
      localStorage.getItem(EDITOR_ONBOARDING_STORAGE_COMPLETED) === "true"
    ) {
      return false;
    }
    if (localStorage.getItem(EDITOR_ONBOARDING_STORAGE_DISMISSED) === "true") {
      return false;
    }
    return searchParams.get("tutorial") === "1";
  }, [storageReady, searchParams]);

  const editorWelcomeCopy = useMemo(() => {
    const r = searchParams.get("onboarding_role") as OnboardingRole | null;
    if (!r) return null;
    return getEditorWelcomeForRole(r);
  }, [searchParams]);

  // Get drill and DrillCanvas from context for the title and canvas rendering
  const { drill, DrillCanvas } = useDrillViewData();
  const { fieldEditorData, setFieldEditorData } = useFieldEditorData();
  const { drillDocumentData, updateDrillDocumentData } = useDrillDocumentData();
  const { exportEngine, exportCanvas } = useExportData();

  const selectedPerformerCount =
    drill?.engine?.selectedPerformers?.length ?? 0;

  const cancelDeletePerformers = () => {
    setFieldEditorData({
      ...fieldEditorData,
      showDeletePerformersModal: false,
    });
  };

  const confirmDeletePerformers = () => {
    if (!drill?.engine?.selectedPerformers?.length) {
      cancelDeletePerformers();
      return;
    }

    const performersToDelete = [...drill.engine.selectedPerformers];
    const count = performersToDelete.length;

    performersToDelete.forEach((performer) => {
      drill.removePerformer(performer);
    });
    drill.engine.clearSelection();

    toast.success(
      count === 1
        ? "Performer deleted"
        : `${count} performers deleted`
    );

    setFieldEditorData({
      ...fieldEditorData,
      showDeletePerformersModal: false,
    });
  };

  return (
    <div className="h-screen max-h-screen m-0 p-0 bg-whitew-full font-plusJakarta overflow-none">
      <Helmet>
        <title>
          {`${(drillDocumentData?.title?.trim()
            ? drillDocumentData.title
            : drill?.title) || "Loading..."} - DrillFlo Project`}
        </title>
      </Helmet>
      <div className="h-screen max-h-full flex flex-col">
        <FieldEditorHeader />
        <FieldEditorMenuBar />
        <div className="flex flex-row flex-1 min-h-0 overflow-hidden">
          <FieldEditorModeSelect />
          <div className="flex-1 min-w-0 overflow-hidden">{DrillCanvas}</div>
          <FieldEditorRightNav />
        </div>
        <FieldEditorContextPopup />

        <FieldEditorTimeline />
        {fieldEditorData.showAudioTrackModal && (
          <AudioSettingsModal
            audioUrl={drillDocumentData?.audioFile || ""}
            showAudioTrackModal={fieldEditorData.showAudioTrackModal}
            fieldEditorData={fieldEditorData}
            setFieldEditorData={setFieldEditorData}
            slug={slug as string}
            drillDocumentData={drillDocumentData as DrillDocumentData}
            updateDrillDocumentData={updateDrillDocumentData}
            setAudioUrl={() => { }}
          />
        )}

        {fieldEditorData.showFeildSettingsModal && (
          <FieldSettingsModal
            onClose={() =>
              setFieldEditorData({
                ...fieldEditorData,
                showFeildSettingsModal: false,
              })
            }
          />
        )}

        {fieldEditorData.showPerformerLabelModal && (
          <PerformerModal
            onClose={() =>
              setFieldEditorData({
                ...fieldEditorData,
                showPerformerLabelModal: false,
              })
            }
          />
        )}

        {fieldEditorData.showCoordinateSheetModal && (
          <CoordinateModal
            onClose={() =>
              setFieldEditorData({
                ...fieldEditorData,
                showCoordinateSheetModal: false,
              })
            }
          />
        )}

        {fieldEditorData.showBulkEditPerformerLabelModal && (
          <BulkEditPerformerLabelModal
            onClose={() =>
              setFieldEditorData({
                ...fieldEditorData,
                showBulkEditPerformerLabelModal: false,
              })
            }
          />
        )}

        {fieldEditorData.showExportDialog && (
          <PrintPreview
            exportCanvas={exportCanvas}
            setShowExportDialog={() =>
              setFieldEditorData({
                ...fieldEditorData,
                showExportDialog: false,
              })
            }
            exportEngine={exportEngine}
          />
        )}

        {fieldEditorData.isShortcutsOpen && (
          <ShortcutsList
            onClose={() =>
              setFieldEditorData({
                ...fieldEditorData,
                isShortcutsOpen: false,
              })
            }
            isOpen={fieldEditorData.isShortcutsOpen}
          />
        )}

        {fieldEditorData.showDeletePerformersModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
            <div className="bg-white p-8 rounded-xl shadow-lg max-w-md w-full mx-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="text-red-600"
                >
                  <path d="M3 6h18"></path>
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                  <line x1="10" y1="11" x2="10" y2="17"></line>
                  <line x1="14" y1="11" x2="14" y2="17"></line>
                </svg>
              </div>
              <h2 className="text-xl font-bold text-center mb-2">
                Delete {selectedPerformerCount === 1 ? "Performer" : "Performers"}
              </h2>
              <p className="text-gray-600 text-center mb-6">
                Are you sure you want to delete{" "}
                <span className="font-semibold">
                  {selectedPerformerCount === 1
                    ? "this performer"
                    : `${selectedPerformerCount} performers`}
                </span>
                ? This action cannot be undone.
              </p>
              <div className="flex justify-end space-x-4">
                <button
                  onClick={cancelDeletePerformers}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDeletePerformers}
                  autoFocus
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  Delete {selectedPerformerCount === 1 ? "Performer" : "Performers"}
                </button>
              </div>
            </div>
          </div>
        )}

        <EditorOnboardingOverlay
          active={showEditorOnboarding}
          welcomeCopy={editorWelcomeCopy}
        />
        {/* <TrialActivationModal /> */}
      </div>
    </div>
  );
}

export default function FieldEditor3() {
  return (
    <FieldEditorProvider>
      <FieldEditor3Content />
    </FieldEditorProvider>
  );
}
